"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  createAndEnrichCompanyContext, 
  getCompanyContext, 
  estimateLocation, 
  getLocationFromIP,
  LocationEstimate,
  markOnboardingComplete
} from "@/services/companyContextService";
import { saveCompanyInfo } from "@/services/companyService";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faUsers, faBuilding, faSparkles, faLocationDot, faLocationCrosshairs, faKeyboard, faXmark, faIdCard } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { DuckLoadingScreen } from "@/components/DuckLoadingScreen";
import { getAuthUID } from "@/lib/authState";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

interface CompanyContextDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

// Feature flag to enable/disable ABN/ACN capture (DED-1564)
const ENABLE_ABN_ACN_CAPTURE = false;

export default function CompanyContextDialog({ open, onClose, onComplete }: CompanyContextDialogProps) {
  const [companyName, setCompanyName] = React.useState("");
  const [domain, setDomain] = React.useState("");
  const [abnAcn, setAbnAcn] = React.useState("");
  const [staff, setStaff] = React.useState("");
  const [offices, setOffices] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  
  // Location state
  const [showLocationDialog, setShowLocationDialog] = React.useState(false);
  const [locationData, setLocationData] = React.useState<LocationEstimate | null>(null);
  const [showManualLocation, setShowManualLocation] = React.useState(false);
  const [showLocationConfirm, setShowLocationConfirm] = React.useState(false);
  const [manualCity, setManualCity] = React.useState("");
  const [manualState, setManualState] = React.useState("");
  const [manualCountry, setManualCountry] = React.useState("");
  const [geoLoading, setGeoLoading] = React.useState(false);
  const [ipLoading, setIpLoading] = React.useState(false);

  const staffValid = staff === "" || /^[0-9]+$/.test(staff);
  const officesValid = offices === "" || /^[0-9]+$/.test(offices);
  
  // Domain validation - must be a valid domain format (e.g., example.com, sub.example.co.uk)
  // Allows optional protocol prefix which will be stripped
  const domainValid = React.useMemo(() => {
    if (!domain.trim()) return false;
    // Strip protocol if present
    let cleanDomain = domain.trim().toLowerCase();
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '');
    // Remove trailing slashes and paths
    cleanDomain = cleanDomain.split('/')[0];
    // Domain regex: allows subdomains, requires at least one dot, valid TLD
    const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;
    return domainRegex.test(cleanDomain);
  }, [domain]);
  
  const canContinue = domain.trim().length > 0 && domainValid && staff.trim().length > 0 && offices.trim().length > 0 && staffValid && officesValid && !saving;

  // Load existing context on mount
  React.useEffect(() => {
    const loadContext = async () => {
      try {
        const companyID = (window as any).companyID;
        
        if (!companyID) {
          setLoading(false);
          return;
        }
        
        // Check Firestore for existing saved context
        const existing = await getCompanyContext(companyID);
        if (existing) {
          setCompanyName(existing.identity.name || "");
          setDomain(existing.identity.domain || "");
          setStaff(existing.metrics.employeeCount?.toString() || "");
          setOffices(existing.metrics.officeCount?.toString() || "");
        }
      } catch (error) {
        console.error("[CompanyContextDialog] Failed to load existing context:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (open) {
      loadContext();
    }
  }, [open]);

  // Handle "Next" button - show location dialog first
  const handleNext = () => {
    setShowLocationDialog(true);
  };

  // Handle browser geolocation permission
  const handleAllowLocation = async () => {
    setGeoLoading(true);
    try {
      // This will trigger the browser permission prompt and wait for user response
      const location = await estimateLocation();
      if (location) {
        setLocationData(location);
        // Pre-fill manual fields for editing
        setManualCity(location.city || "");
        setManualState(location.state || "");
        setManualCountry(location.country || "");
        // Show confirmation step
        setShowLocationConfirm(true);
      } else {
        // User denied permission, fall back to IP
        await handleUseIPLocation();
      }
    } catch (error) {
      console.error("[CompanyContextDialog] Geolocation failed:", error);
      // Fall back to IP on error
      await handleUseIPLocation();
    } finally {
      setGeoLoading(false);
    }
  };

  // Handle IP-based location (skip browser permission)
  const handleUseIPLocation = async () => {
    setIpLoading(true);
    try {
      const ipLocation = await getLocationFromIP();
      if (ipLocation) {
        const location: LocationEstimate = {
          ...ipLocation,
          isEstimated: true,
          estimationMethod: 'ip_lookup',
        } as LocationEstimate;
        setLocationData(location);
        // Pre-fill manual fields for editing
        setManualCity(location.city || "");
        setManualState(location.state || "");
        setManualCountry(location.country || "");
        // Show confirmation step
        setShowLocationConfirm(true);
      } else {
        // No IP location available, just proceed without location
        setShowLocationDialog(false);
        await saveContext(null);
      }
    } catch (error) {
      console.error("[CompanyContextDialog] IP location failed:", error);
      setShowLocationDialog(false);
      await saveContext(null);
    } finally {
      setIpLoading(false);
    }
  };

  // Handle manual location entry
  const handleManualLocation = () => {
    setShowManualLocation(true);
  };

  // Confirm the detected location and save
  const handleConfirmLocation = async () => {
    // Check if user edited the values
    const wasEdited = manualCity.trim() !== (locationData?.city || "") || 
                      manualState.trim() !== (locationData?.state || "") ||
                      manualCountry.trim() !== (locationData?.country || "");
    
    // Use the edited values from manual fields (user may have corrected them)
    const location: LocationEstimate = {
      ...locationData,
      city: manualCity.trim() || locationData?.city,
      state: manualState.trim() || locationData?.state,
      country: manualCountry.trim() || locationData?.country,
      // If user edited, mark as user_input, otherwise keep original method
      isEstimated: wasEdited ? false : locationData?.isEstimated ?? true,
      // Mark as confirmed since user went through confirmation step
      isConfirmed: true,
      estimationMethod: wasEdited ? 'user_input' : locationData?.estimationMethod ?? 'geolocation',
    };
    setShowLocationDialog(false);
    setShowLocationConfirm(false);
    await saveContext(location);
  };

  // Save manual location and proceed
  const handleSaveManualLocation = async () => {
    const location: LocationEstimate = {
      city: manualCity.trim() || undefined,
      state: manualState.trim() || undefined,
      country: manualCountry.trim() || undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isEstimated: false,
      isConfirmed: true, // User explicitly entered this
      estimationMethod: 'user_input',
    };
    setLocationData(location);
    setShowLocationDialog(false);
    setShowManualLocation(false);
    await saveContext(location);
  };

  // Actual save function
  const saveContext = async (location: LocationEstimate | null) => {
    const companyID = (window as any).companyID;
    if (!companyID) {
      toast.error("Company ID not found");
      return;
    }

    setSaving(true);
    try {
      const userId = getAuthUID() || undefined;
      
      // Create company context and auto-enrich with HubSpot data
      const result = await createAndEnrichCompanyContext(companyID, {
        domain: domain.trim(),
        companyName: companyName.trim() || undefined,
        abnAcn: abnAcn.trim() || undefined,
        employeeCount: staff ? parseInt(staff) : undefined,
        officeCount: offices ? parseInt(offices) : undefined,
        location,
      }, userId);

      // Log enrichment results for debugging
      if (result.enrichment.success) {
        console.log("[CompanyContextDialog] HubSpot enrichment successful:", result.enrichment.data?.properties);
      } else if (result.enrichment.notFound) {
        console.log("[CompanyContextDialog] No HubSpot data found for domain:", domain.trim());
      } else {
        console.warn("[CompanyContextDialog] HubSpot enrichment failed:", result.enrichment.error);
      }
      
      // Mark onboarding as complete
      await markOnboardingComplete(companyID);
      
      // Show success toast
      toast.success("Company context completed successfully!");

      // Also save to main company document (same as GeneralSection in settings)
      // This ensures website, city, country, abnAcn appear in company settings
      try {
        const companyUpdate: Record<string, any> = {
          website: domain.trim(),
          lastUpdatedBy: userId,
        };
        
        // Add ABN/ACN if provided
        if (abnAcn.trim()) {
          companyUpdate.abnAcn = abnAcn.trim();
        }
        
        // Add location data if available
        if (location) {
          if (location.city) companyUpdate.city = location.city;
          if (location.country) companyUpdate.country = location.country;
          if (location.state) companyUpdate.state = location.state;
        }
        
        await saveCompanyInfo(companyID, {
          set: companyUpdate,
          merge: true
        });
        console.log("[CompanyContextDialog] Also saved to main company document:", companyUpdate);
      } catch (err) {
        console.warn("[CompanyContextDialog] Failed to save to main company document:", err);
        // Don't fail the whole operation if this secondary save fails
      }

      // Close dialog and call onComplete callback
      onClose();
      onComplete();
    } catch (error) {
      console.error("[CompanyContextDialog] Failed to save:", error);
      toast.error("Failed to save company context");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent 
          className="max-w-4xl! w-4xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onOpenAutoFocus={(e) => e.preventDefault()}
          noScroll
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full h-[calc(80vh-4rem)] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {loading && (
              <div className="absolute inset-0 z-50 rounded-xl overflow-hidden">
                <DuckLoadingScreen 
                  variant="overlay"
                  title="Loading Company Context..." 
                  text="Fetching your existing company information"
                  duckFacts={[
                    "Quack! Checking your company details...",
                    "Looking up your domain info...",
                    "Almost there!"
                  ]}
                />
              </div>
            )}
            
            {/* Header - shrink-0 keeps it fixed size */}
            <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-amber-500/20 to-orange-500/20 mb-4">
                <FontAwesomeIcon icon={faSparkles} className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">Complete your company context</h2>
              <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                This information is required to enable AI features and helps us provide better compliance recommendations.
              </p>
            </div>

            {/* Form Content - scrollable area */}
            <div className="p-8 flex-1 min-h-0 overflow-y-auto space-y-6">
              {!loading && (
                <>
                  {/* Domain Card */}
                  <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-amber-500/15 to-orange-500/15 flex items-center justify-center">
                        <FontAwesomeIcon icon={faGlobe} className="w-6 h-6 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <label htmlFor="domain" className="block text-sm font-semibold text-foreground mb-1">
                          Company Domain <span className="text-destructive">*</span>
                        </label>
                        <p className="text-xs text-muted-foreground mb-3">
                          Your primary website domain (e.g., example.com)
                        </p>
                        <Input 
                          id="domain" 
                          placeholder="yourcompany.com" 
                          value={domain} 
                          onChange={e => setDomain(e.target.value)}
                          className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50" 
                        />
                        {domain.trim() && !domainValid && <div className="text-xs text-destructive mt-2">Please enter a valid domain (e.g., example.com)</div>}
                      </div>
                    </div>
                  </div>

                  {/* Company Name Card */}
                  <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <FontAwesomeIcon icon={faBuilding} className="w-6 h-6 text-purple-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <label htmlFor="companyName" className="block text-sm font-semibold text-foreground mb-1">
                          Company Name
                        </label>
                        <p className="text-xs text-muted-foreground mb-3">
                          Your legal company name (optional)
                        </p>
                        <Input 
                          id="companyName" 
                          placeholder="Your Company Ltd" 
                          value={companyName} 
                          onChange={e => setCompanyName(e.target.value)}
                          className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50" 
                        />
                      </div>
                    </div>
                  </div>

                  {/* ABN/ACN Card - conditionally rendered based on feature flag */}
                  {ENABLE_ABN_ACN_CAPTURE && (
                    <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                      <div className="flex items-start gap-4">
                        <div className="shrink-0 w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                          <FontAwesomeIcon icon={faIdCard} className="w-6 h-6 text-purple-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <label htmlFor="abnAcn" className="block text-sm font-semibold text-foreground mb-1">
                            ABN / ACN
                          </label>
                          <p className="text-xs text-muted-foreground mb-3">
                            Your Australian Business Number or Company Number (optional)
                          </p>
                          <Input 
                            id="abnAcn" 
                            placeholder="e.g., 12 345 678 901" 
                            value={abnAcn} 
                            onChange={e => setAbnAcn(e.target.value)}
                            className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50" 
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Staff Count Card */}
                  <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <FontAwesomeIcon icon={faUsers} className="w-6 h-6 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <label htmlFor="staff" className="block text-sm font-semibold text-foreground mb-1">
                          Team Size <span className="text-destructive">*</span>
                        </label>
                        <p className="text-xs text-muted-foreground mb-3">
                          Approximate number of employees in your organisation
                        </p>
                        <Input 
                          id="staff" 
                          placeholder="e.g., 50" 
                          value={staff} 
                          onChange={e => setStaff(e.target.value)}
                          className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50" 
                        />
                        {!staffValid && <div className="text-xs text-destructive mt-2">Please enter a valid number</div>}
                      </div>
                    </div>
                  </div>

                  {/* Offices Card */}
                  <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <FontAwesomeIcon icon={faBuilding} className="w-6 h-6 text-emerald-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <label htmlFor="offices" className="block text-sm font-semibold text-foreground mb-1">
                          Office Locations <span className="text-destructive">*</span>
                        </label>
                        <p className="text-xs text-muted-foreground mb-3">
                          Number of physical office locations or sites
                        </p>
                        <Input 
                          id="offices" 
                          placeholder="e.g., 3" 
                          value={offices} 
                          onChange={e => setOffices(e.target.value)}
                          className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50" 
                        />
                        {!officesValid && <div className="text-xs text-destructive mt-2">Please enter a valid number</div>}
                      </div>
                    </div>
                  </div>

                  {/* Info Note */}
                  <div className="rounded-lg bg-primary/5 border border-primary/10 p-4 flex items-start gap-3">
                    <FontAwesomeIcon icon={faSparkles} className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">Why do we need this?</span> Your company size and structure helps us suggest the most relevant compliance controls and processes for your organisation.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Footer - flex-shrink-0 keeps it fixed size */}
            <div className="border-t border-white/10 px-8 py-5 bg-card flex justify-between items-center shrink-0">
              <p className="text-xs text-muted-foreground">
                {!domain.trim() 
                  ? "Enter your domain to continue" 
                  : !domainValid
                    ? "Enter a valid domain (e.g., example.com)"
                    : !staff.trim() || !staffValid
                      ? "Enter your team size to continue"
                      : !offices.trim() || !officesValid
                        ? "Enter your office locations to continue"
                        : "Ready to continue"}
              </p>
              <Button disabled={!canContinue} onClick={handleNext} className="min-w-[100px]">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Permission Dialog */}
      <AlertDialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <AlertDialogContent className="max-w-md bg-card">
          <AlertDialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-linear-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
                <FontAwesomeIcon icon={faLocationDot} className="w-8 h-8 text-amber-500" />
              </div>
            </div>
            <AlertDialogTitle className="text-center">Where is your head office located?</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              Knowing your location helps us suggest region-specific compliance requirements and data residency options. 
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Step 1: Choose location method */}
          {!showManualLocation && !showLocationConfirm && (
            <div className="space-y-3 mt-4">
              <Button 
                onClick={handleAllowLocation} 
                disabled={geoLoading || ipLoading}
                className="w-full justify-start gap-3 h-auto py-3"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faLocationCrosshairs} className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="font-semibold">Use my location</div>
                  <div className="text-xs text-primary-foreground/70 font-normal">Most accurate - requires permission</div>
                </div>
                {geoLoading && <Loader2 className="ml-auto h-4 w-4 animate-spin" />}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleManualLocation}
                disabled={geoLoading || ipLoading}
                className="w-full justify-start gap-3 h-auto py-3"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faKeyboard} className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <div className="font-semibold">Enter manually</div>
                  <div className="text-xs text-muted-foreground font-normal">Type your city and country</div>
                </div>
              </Button>
              
              <Button 
                variant="ghost" 
                onClick={handleUseIPLocation}
                disabled={geoLoading || ipLoading}
                className="w-full justify-start gap-3 h-auto py-3 text-muted-foreground"
              >
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faGlobe} className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="font-semibold">Skip</div>
                  <div className="text-xs text-muted-foreground/70 font-normal">You can add this later</div>
                </div>
                {ipLoading && <Loader2 className="ml-auto h-4 w-4 animate-spin" />}
              </Button>
            </div>
          )}

          {/* Step 2: Confirm detected location */}
          {showLocationConfirm && (
            <div className="space-y-4 mt-4">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">We detected your location as:</p>
                <p className="text-lg font-semibold text-foreground">
                  {locationData?.city}{locationData?.city && locationData?.country ? ", " : ""}{locationData?.country}
                </p>
                {locationData?.state && (
                  <p className="text-sm text-muted-foreground">{locationData.state}</p>
                )}
              </div>
              
              <p className="text-sm text-muted-foreground text-center">
                Is this correct? You can edit it below if needed.
              </p>
              
              <div className="space-y-3">
                <div>
                  <label htmlFor="confirmCity" className="block text-sm font-medium mb-1">City</label>
                  <Input 
                    id="confirmCity"
                    placeholder="e.g., Sydney"
                    value={manualCity}
                    onChange={e => setManualCity(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="confirmState" className="block text-sm font-medium mb-1">State / Province</label>
                  <Input 
                    id="confirmState"
                    placeholder="e.g., New South Wales"
                    value={manualState}
                    onChange={e => setManualState(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="confirmCountry" className="block text-sm font-medium mb-1">Country</label>
                  <Input 
                    id="confirmCountry"
                    placeholder="e.g., Australia"
                    value={manualCountry}
                    onChange={e => setManualCountry(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowLocationConfirm(false)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleConfirmLocation}
                  className="flex-1"
                  disabled={saving}
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirm & Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Manual location entry */}
          {showManualLocation && (
            <div className="space-y-4 mt-4">
              <div className="space-y-3">
                <div>
                  <label htmlFor="manualCity" className="block text-sm font-medium mb-1">City</label>
                  <Input 
                    id="manualCity"
                    placeholder="e.g., Sydney"
                    value={manualCity}
                    onChange={e => setManualCity(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="manualState" className="block text-sm font-medium mb-1">State / Province</label>
                  <Input 
                    id="manualState"
                    placeholder="e.g., New South Wales"
                    value={manualState}
                    onChange={e => setManualState(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="manualCountry" className="block text-sm font-medium mb-1">Country</label>
                  <Input 
                    id="manualCountry"
                    placeholder="e.g., Australia"
                    value={manualCountry}
                    onChange={e => setManualCountry(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowManualLocation(false)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleSaveManualLocation}
                  className="flex-1"
                  disabled={saving}
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save & Continue
                </Button>
              </div>
            </div>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
